/**
 * For Business — commercial / workplace commission inquiries.
 */

type RuntimeConfig = { apiEndpoint?: string };

type LineupPiece = {
  id: string;
  name: string;
  seats: string;
  description: string;
  image: string;
};

type Lineup = { pieces: LineupPiece[] };

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const KNOWN_PIECE_TYPES = new Set([
  "",
  "conference",
  "boardroom",
  "reception",
  "bench_credenza",
  "other",
]);

let apiBase = "";
let configLoaded = false;
let lineup: Lineup | null = null;

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function setStatus(
  message: string,
  state: "idle" | "success" | "error" | "loading",
): void {
  const el = document.querySelector<HTMLElement>("#form-status");
  if (!el) return;
  if (!message || state === "idle") {
    el.hidden = true;
    el.textContent = "";
    el.dataset.state = "idle";
    return;
  }
  el.hidden = false;
  el.textContent = message;
  el.dataset.state = state;
}

async function loadConfig(): Promise<void> {
  try {
    const res = await fetch("config.json", { cache: "no-store" });
    if (!res.ok) throw new Error(String(res.status));
    const cfg = (await res.json()) as RuntimeConfig;
    apiBase = (cfg.apiEndpoint ?? "").replace(/\/$/, "");
    configLoaded = true;
  } catch (err) {
    console.warn("[for-business] config", err);
    configLoaded = false;
  }
}

async function loadLineup(): Promise<void> {
  const res = await fetch("commercial-lineup.json", { cache: "no-store" });
  if (!res.ok) throw new Error("Could not load lineup.");
  lineup = (await res.json()) as Lineup;
}

function selectPieceType(id: string): void {
  const select = document.querySelector<HTMLSelectElement>("#pieceType");
  if (!select) return;
  const option = Array.from(select.options).find((o) => o.value === id);
  if (option) select.value = id;
}

function scrollToInquiry(): void {
  document.querySelector("#inquiry")?.scrollIntoView({ behavior: "smooth" });
}

function renderLineup(): void {
  const list = document.querySelector("#lineup-list");
  const select = document.querySelector<HTMLSelectElement>("#pieceType");
  if (!list || !lineup) return;

  list.innerHTML = lineup.pieces
    .map(
      (p) => `<li>
        <button type="button" class="lineup-item" data-piece="${escapeHtml(p.id)}">
          <span class="lineup-item__media">
            <img
              class="lineup-item__img"
              src="${escapeHtml(p.image)}"
              alt=""
              width="280"
              height="200"
              loading="lazy"
            />
            <p class="lineup-item__caption">Design concept</p>
          </span>
          <span>
            <h3 class="lineup-item__name">${escapeHtml(p.name)}</h3>
            <p class="lineup-item__meta">${escapeHtml(p.seats)}</p>
            <p class="lineup-item__desc">${escapeHtml(p.description)}</p>
          </span>
        </button>
      </li>`,
    )
    .join("");

  if (select) {
    const current = select.value;
    select.innerHTML =
      `<option value="">Select a starting point</option>` +
      lineup.pieces
        .map(
          (p) =>
            `<option value="${escapeHtml(p.id)}">${escapeHtml(p.name)}</option>`,
        )
        .join("") +
      `<option value="other">Other / custom commercial</option>`;
    if (current) select.value = current;
  }

  list.querySelectorAll<HTMLButtonElement>("[data-piece]").forEach((btn) => {
    btn.addEventListener("click", () => {
      const id = btn.dataset.piece ?? "";
      selectPieceType(id);
      scrollToInquiry();
      document.querySelector<HTMLInputElement>("#name")?.focus();
    });
  });
}

function setupNavToggle(): void {
  const toggle = document.querySelector<HTMLButtonElement>(".nav__toggle");
  const menu = document.querySelector<HTMLElement>("#nav-menu");
  if (!toggle || !menu) return;
  toggle.addEventListener("click", () => {
    const open = menu.dataset.open === "true";
    menu.dataset.open = open ? "false" : "true";
    toggle.setAttribute("aria-expanded", open ? "false" : "true");
  });
}

function setupForm(): void {
  const form = document.querySelector<HTMLFormElement>("#commercial-form");
  if (!form) return;
  const submitBtn = form.querySelector<HTMLButtonElement>('button[type="submit"]');

  form.addEventListener("submit", async (ev) => {
    ev.preventDefault();
    if (!submitBtn) return;

    const fd = new FormData(form);
    const company = String(fd.get("company") ?? "").trim();
    if (company) {
      form.reset();
      setStatus(
        "Thanks. Your inquiry is on its way. We'll be in touch within 2 business days.",
        "success",
      );
      return;
    }
    const name = String(fd.get("name") ?? "").trim();
    const businessName = String(fd.get("businessName") ?? "").trim();
    const email = String(fd.get("email") ?? "").trim();
    const phone = String(fd.get("phone") ?? "").trim();
    const pieceType = String(fd.get("pieceType") ?? "").trim();
    const headcountOrSize = String(fd.get("headcountOrSize") ?? "").trim();
    const timeline = String(fd.get("timeline") ?? "").trim();
    const message = String(fd.get("message") ?? "").trim();

    if (!name || !businessName || !EMAIL_RE.test(email) || message.length < 10) {
      setStatus(
        "Please complete name, business, email, and a short message.",
        "error",
      );
      return;
    }
    if (!KNOWN_PIECE_TYPES.has(pieceType)) {
      setStatus("Please choose a valid starting point.", "error");
      return;
    }
    if (!configLoaded) {
      setStatus(
        "Form is not configured yet. Please email us directly (see footer).",
        "error",
      );
      return;
    }

    submitBtn.disabled = true;
    setStatus("Sending…", "loading");
    try {
      const res = await fetch(`${apiBase}/contact`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          source: "commercial",
          name,
          businessName,
          email,
          phone,
          pieceType,
          headcountOrSize,
          timeline,
          message,
        }),
      });
      const data = (await res.json().catch(() => ({}))) as {
        ok?: boolean;
        error?: string;
      };
      if (!res.ok || !data.ok) {
        throw new Error(data.error || "Could not send inquiry.");
      }
      form.reset();
      setStatus(
        "Thanks. Your inquiry is on its way. We'll be in touch within 2 business days.",
        "success",
      );
    } catch (err) {
      setStatus(
        err instanceof Error ? err.message : "Could not send inquiry.",
        "error",
      );
    } finally {
      submitBtn.disabled = false;
    }
  });
}

async function boot(): Promise<void> {
  await loadConfig();
  try {
    await loadLineup();
    renderLineup();
  } catch (err) {
    console.error("[for-business] lineup", err);
  }

  const params = new URLSearchParams(window.location.search);
  const piece = params.get("piece");
  if (piece) {
    selectPieceType(piece);
  }

  setupNavToggle();
  setupForm();
}

void boot();
